import { create } from 'zustand';

interface User {
  sub: string;
  email: string;
  name?: string;
  roles?: string[];
  organization?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: () => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
  setUser: (user: User | null) => void;
}

// Dev mode mock user
const DEV_USER: User = {
  sub: 'dev-user-001',
  email: 'dev@insuscan.ai',
  name: 'Dev User',
  roles: ['admin', 'user'],
  organization: 'InsuScan Dev',
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  
  login: () => {
    // In dev mode, auto-login with mock user
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isDev) {
      set({
        user: DEV_USER,
        isAuthenticated: true,
        isLoading: false,
      });
      return;
    }
    window.location.href = '/api/v1/auth/login';
  },
  
  logout: async () => {
    try {
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isDev) {
        await fetch('/api/v1/auth/logout');
      }
      set({ user: null, isAuthenticated: false });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  },
  
  checkAuth: async () => {
    try {
      set({ isLoading: true, error: null });
      
      // In dev mode, use mock user
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isDev) {
        set({
          user: DEV_USER,
          isAuthenticated: true,
          isLoading: false,
        });
        return;
      }
      
      const response = await fetch('/api/v1/auth/me', {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        set({
          user: data.data || data,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Failed to check authentication',
      });
    }
  },
  
  setUser: (user) => {
    set({
      user,
      isAuthenticated: !!user,
    });
  },
}));
